import { useState, useEffect } from 'react';

import { CMS_API_URL } from '../config/apiBase';

/**
 * Fetch all variations available at a given school for a brand.
 * Backed by GET /api/schools/{brandSlug}/{schoolSlug}/variations.
 *
 * Returns { school, variations } where each variation is a "product-like"
 * payload (with pinned_attributes + available_options).
 */
export default function useSchoolVariations(brandSlug, schoolSlug, className = null, shopSlug = null, groupSlug = null) {
  const [data, setData]       = useState({ school: null, shop: null, group: null, collections: [], menuItemImages: {}, variations: [], packages: [], shops: [], layout: 'combined', nextShopClosesAt: null, paymentDeadline: null, nextShopClosesAtDisplay: null, paymentDeadlineDisplay: null, shopClosed: false });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!brandSlug || !schoolSlug) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (className) params.set('class', className);
        if (shopSlug)  params.set('shop', shopSlug);
        // Group ("sub-shop") page: the BE narrows variations + packages to the
        // group's collections (only meaningful together with ?shop=).
        if (shopSlug && groupSlug) params.set('group', groupSlug);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const res = await fetch(
          `${CMS_API_URL}/api/schools/${encodeURIComponent(brandSlug)}/${encodeURIComponent(schoolSlug)}/variations${qs}`,
          { signal: controller.signal }
        );

        if (!res.ok) throw new Error(`School variations API returned ${res.status}`);

        const json = await res.json();
        if (!cancelled) {
          setData({
            school: json.school ?? null,
            // shop carries `layout` (effective) + `groups[]`; group is the
            // requested sub-shop or null; collections[] = ordered sections
            // {id, name, displayName, subtitle, groupId, variationIds[]} for the
            // grouped layout; menuItemImages = per-menu-item header photos.
            shop: json.shop ?? null,
            group: json.group ?? null,
            collections: Array.isArray(json.collections) ? json.collections : [],
            menuItemImages: json.menuItemImages && typeof json.menuItemImages === 'object' ? json.menuItemImages : {},
            variations: Array.isArray(json.variations) ? json.variations : [],
            packages: Array.isArray(json.packages) ? json.packages : [],
            shops: Array.isArray(json.shops) ? json.shops : [],
            layout: json.school?.layout ?? 'combined',
            nextShopClosesAt: json.nextShopClosesAt ?? null,
            paymentDeadline: json.paymentDeadline ?? null,
            nextShopClosesAtDisplay: json.nextShopClosesAtDisplay ?? null,
            paymentDeadlineDisplay: json.paymentDeadlineDisplay ?? null,
            // BE marker: the requested shop's date window has passed (or not
            // started) — render the localized closed page, not an empty grid.
            shopClosed: json.shopClosed === true,
          });
        }
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          console.error(`[useSchoolVariations] Failed for "${brandSlug}/${schoolSlug}":`, err);
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [brandSlug, schoolSlug, className, shopSlug, groupSlug]);

  return { ...data, loading, error };
}
